/**
 * CascadeStep  (SM-21 / DA-2294)
 *
 * A single position in a cascading picker (target / operator / value / ...). Thin
 * wrapper over SearchableSelect so every builder (HP, AP, ...) gets the same look —
 * the cascading behavior itself (gating + reset) lives one level up, in CascadeRow.
 */
import React from 'react'
import { Box, Typography } from '@mui/material'
import { SearchableSelect } from 'components/common/SearchableSelect'
import { CascadeStepDef } from './cascadeTypes'

export interface CascadeStepProps {
  step: CascadeStepDef
  value: string
  onChange: (value: string) => void
  disabled?: boolean
  /** Catalog still loading — keeps the step from collapsing to the empty/locked state. */
  loading?: boolean
}

export const CascadeStep: React.FC<CascadeStepProps> = ({ step, value, onChange, disabled = false, loading = false }) => {
  // While the catalog loads, empty options are expected — don't collapse to the locked/empty
  // state; let the SearchableSelect show its (announced) loading text instead.
  const isLocked = disabled || (!loading && step.options.length === 0)

  // Forward-guard (DA-2184): a scope-pinned target renders as a fixed, read-only label —
  // same block layout as the disabledReason case, but showing the chosen value rather than
  // a "pick an upstream first" hint. Only the target step is ever `locked`.
  if (step.locked) {
    return (
      <Box sx={{ minWidth: 160 }}>
        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}>
          {step.label}
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.primary' }}>
          {step.lockedLabel}
        </Typography>
      </Box>
    )
  }

  return (
    <Box sx={{ minWidth: 160 }}>
      {isLocked && step.disabledReason ? (
        <Box sx={{ minWidth: 160 }}>
          <Typography variant="caption" sx={{ color: 'text.disabled', display: 'block', mb: 0.5 }}>
            {step.label}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.disabled', fontStyle: 'italic' }}>
            {step.disabledReason}
          </Typography>
        </Box>
      ) : (
        <SearchableSelect
          label={step.label}
          value={value}
          onChange={onChange}
          options={step.options}
          disabled={isLocked}
          loading={loading}
          placeholder={step.placeholder}
          noOptionsText="No options available"
          fullWidth
        />
      )}
    </Box>
  )
}
